// Manager Policies tab (PO 2026-08-06) — read-only mirror of HR's Policies
// list (`HrPolicies.tsx`). Only `Approved` policies are ever returned by
// `GET /api/manager/policies` (HR's own review gate — a Draft upload hasn't
// been signed off yet), so there's no approval pill or Draft state to render
// here, and no upload/approve/delete affordance — those stay HR_Admin-only.

import { useQuery } from '@tanstack/react-query';
import type { ApprovedPolicySummary } from '@besideu/shared/policies';
import { api } from '@/lib/api.ts';
import { formatBytes, formatDate } from '@/lib/format.ts';

export function ManagerPolicies() {
  const policies = useQuery({
    queryKey: ['manager', 'policies'],
    queryFn: () => api.get<{ policies: ApprovedPolicySummary[] }>('/api/manager/policies'),
  });

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Policies</h1>
        <p className="mt-2 text-muted-foreground">
          Your organisation&rsquo;s bereavement leave policy and related guidance, once HR has
          reviewed and approved it.
        </p>
      </header>

      <PoliciesList
        loading={policies.isPending}
        error={policies.error ? 'Could not load policies.' : null}
        policies={policies.data?.policies ?? []}
      />
    </section>
  );
}

function PoliciesList({
  loading,
  error,
  policies,
}: {
  loading: boolean;
  error: string | null;
  policies: ApprovedPolicySummary[];
}) {
  if (loading) return <p className="text-muted-foreground">Loading policies…</p>;
  if (error) return <p className="text-traffic-red">{error}</p>;
  if (policies.length === 0) {
    return (
      <p className="text-muted-foreground">
        HR hasn&rsquo;t approved a policy document yet — check back once one has been uploaded.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">File</th>
            <th className="px-4 py-2 font-medium">Size</th>
            <th className="px-4 py-2 font-medium">Uploaded</th>
            <th className="px-4 py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => (
            <tr key={p.id} className="border-t border-border">
              <td className="px-4 py-2">{p.filename}</td>
              <td className="px-4 py-2 text-muted-foreground">{formatBytes(p.sizeBytes)}</td>
              <td className="px-4 py-2 text-muted-foreground">{formatDate(p.uploadedAt)}</td>
              <td className="px-4 py-2 text-right whitespace-nowrap">
                <a
                  className="inline-flex min-h-[44px] items-center text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
                  href={`/api/manager/policies/${p.id}`}
                  target="_blank"
                  rel="noopener"
                  aria-label={`Download ${p.filename}`}
                >
                  Download
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
